import type { Position } from 'geojson';
import { mapAnnotationProjection } from '../../../annotations/elevation';
import { withRenderedStaticMapFrame, type StaticMapRendererDependencies } from '../mapRenderer';
import type { OutputPoint, OutputRect, StaticCompositionRenderPlan } from '../planning';

export const mmToPoints = (mm: number): number => (mm * 72) / 25.4;
export const mapPixelsToPoints = (px: number): number => (px * 72) / 96;
/** MapLibre's Web Mercator rendering latitude bound. Geographic source data may validly reach poles. */
export const MAX_MERCATOR_LATITUDE = 85.051129;
export type MapProjection = (position: readonly [number, number], heightM?: number) => OutputPoint;

export function mapFramePdfRect(plan: StaticCompositionRenderPlan): OutputRect {
  const bounds = plan.mapFrame.boundsMm;
  return {
    x: mmToPoints(bounds.x),
    y: mmToPoints(plan.page.heightMm - bounds.y - bounds.height),
    width: mmToPoints(bounds.width),
    height: mmToPoints(bounds.height),
  };
}

export function logicalPointToPdf(
  point: OutputPoint,
  plan: StaticCompositionRenderPlan,
): OutputPoint {
  const frame = mapFramePdfRect(plan);
  return {
    x: frame.x + (point.x / plan.mapFrame.logicalViewport.widthPx) * frame.width,
    y: frame.y + frame.height - (point.y / plan.mapFrame.logicalViewport.heightPx) * frame.height,
  };
}

export const nearestLongitude = (longitude: number, centre: number): number =>
  longitude + 360 * Math.round((centre - longitude) / 360);

/** Unwrap consecutive vertices before projection; never rewrite canonical coordinates. */
export function projectSequence(
  positions: readonly Position[],
  centre: number,
  project: MapProjection,
): OutputPoint[] {
  let previous = centre;
  return positions.map((position) => {
    const longitude = nearestLongitude(position[0]!, previous);
    previous = longitude;
    const latitude = Math.max(
      -MAX_MERCATOR_LATITUDE,
      Math.min(MAX_MERCATOR_LATITUDE, position[1]!),
    );
    const point = project([longitude, latitude]);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y))
      throw new Error('Invalid projected geometry.');
    return point;
  });
}

export function projectionFramePlan(
  plan: StaticCompositionRenderPlan,
): StaticCompositionRenderPlan {
  return {
    ...plan,
    dpi: 96,
    mapFrame: { ...plan.mapFrame, pixelRatio: 1, backingSize: plan.mapFrame.logicalViewport },
  };
}

export async function withVectorProjection<T>(
  plan: StaticCompositionRenderPlan,
  consume: (project: MapProjection) => T | Promise<T>,
  dependencies: StaticMapRendererDependencies = {},
): Promise<T> {
  return withRenderedStaticMapFrame(
    projectionFramePlan(plan),
    '#ffffff',
    [],
    [],
    (_canvas, map) => {
      const projection =
        typeof map.getVerticalFieldOfView === 'function' ? mapAnnotationProjection(map) : undefined;
      return consume((position, heightM = 0) => {
        if (!heightM || !projection) return map.project([...position]);
        const point = projection.projectAtHeight(position, heightM);
        if (point) {
          const check = projection.project(projection.unproject(point));
          if (Math.hypot(check.x - point.x, check.y - point.y) < 0.05) return point;
        }
        return { x: NaN, y: NaN };
      });
    },
    { ...dependencies, sceneMode: 'projection' },
  );
}
